import { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuthStore } from '../store/auth';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { safeReturnTo } from '../lib/returnTo';
import logoSrc from '../img/SB-logo01.jpg';

/** Map raw Amplify/Cognito errors to user-friendly German messages */
function mapAuthError(err: any): string {
  const msg: string = err?.message ?? '';
  if (/network\s*error/i.test(msg)) {
    return 'Netzwerkfehler – Bitte prüfen Sie Ihre Internetverbindung. Auf iPad/iPhone: Einstellungen → Safari → Verlauf und Websitedaten löschen, dann erneut versuchen.';
  }
  if (/not\s*authorized/i.test(msg) || /incorrect.*password/i.test(msg)) {
    return 'E-Mail oder Passwort ist falsch.';
  }
  if (/user.*not.*found/i.test(msg) || /user.*does.*not.*exist/i.test(msg)) {
    return 'Kein Konto mit dieser E-Mail gefunden.';
  }
  if (/too\s*many\s*(attempts|requests)/i.test(msg)) {
    return 'Zu viele Versuche. Bitte warten Sie einen Moment und versuchen Sie es erneut.';
  }
  return msg || 'Anmeldung fehlgeschlagen';
}

export function LoginPage() {
  useDocumentTitle('Anmelden – Kommunikationsseiten mit Piktogrammen');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const login = useAuthStore((s) => s.login);
  const completeNewPassword = useAuthStore((s) => s.completeNewPassword);
  const nextStep = useAuthStore((s) => s.nextStep);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const navigate = useNavigate();
  const returnTo = safeReturnTo(useLocation().search);

  // Nach der Anmeldung zurück zur aufrufenden Subdomain-App (z. B. Kochbuch), sonst Dashboard
  const goNext = (replace = false) => {
    if (returnTo) window.location.assign(returnTo);
    else navigate('/', { replace });
  };

  useEffect(() => {
    if (isAuthenticated) goNext(true);
  }, [isAuthenticated]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const step = await login(email, password);
      if (step === 'DONE') goNext();
    } catch (err: any) {
      setError(mapAuthError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleNewPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await completeNewPassword(newPassword);
      goNext();
    } catch (err: any) {
      setError(mapAuthError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 py-12">
      <div className="bg-white p-8 rounded-2xl shadow-lg border border-gray-200 w-full max-w-md">
        <div className="text-center mb-8">
          <img src={logoSrc} alt="Symbolbuch.de Logo" className="w-20 h-20 mx-auto mb-4 rounded-2xl object-contain" />
          <h1 className="text-2xl font-bold text-gray-900">Symbolbuch<span className="text-indigo-600">.de</span></h1>
          <p className="text-gray-500 text-sm mt-1">Anmeldung erforderlich</p>
        </div>
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm mb-4">{error}</div>
        )}

        {nextStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED' ? (
          <form onSubmit={handleNewPassword} className="space-y-4">
            <p className="text-sm text-gray-600">
              Bitte wählen Sie ein neues Passwort.
            </p>
            <div>
              <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 mb-1">
                Neues Passwort
              </label>
              <input
                id="newPassword"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                minLength={8}
                className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-gray-900 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-shadow"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-indigo-600 text-white py-3 rounded-lg font-semibold hover:bg-indigo-700 disabled:opacity-50 transition-colors shadow-sm"
            >
              {loading ? 'Wird gespeichert…' : 'Passwort ändern'}
            </button>
          </form>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
              E-Mail
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-gray-900 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-shadow"
            />
          </div>
          <div>
            <label htmlFor="password" className="block text-sm font-semibold text-gray-700 mb-1.5">
              Passwort
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-gray-900 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-shadow"
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-indigo-600 text-white py-3 rounded-lg font-semibold hover:bg-indigo-700 disabled:opacity-50 transition-colors shadow-sm"
          >
            {loading ? 'Wird angemeldet…' : 'Anmelden'}
          </button>
        </form>
        )}

        <div className="mt-6 text-center space-y-2">
          <Link to="/reset-password" className="block text-sm text-gray-500 hover:text-gray-700">
            Passwort vergessen?
          </Link>
          <Link to="/register" className="block text-sm text-indigo-600 hover:text-indigo-700 font-medium">
            Noch kein Zugang? Zugang anfragen
          </Link>
          <Link to="/hallo" className="block text-sm text-indigo-600 hover:text-indigo-700 font-medium">
            Symbolbuch.de – kurz vorgestellt
          </Link>
        </div>
      </div>

      {/* SEO: Sichtbarer Inhalt für Suchmaschinen */}
      <p className="mt-8 max-w-md mx-auto px-4 text-center text-gray-500 text-xs leading-relaxed">
        Symbolbuch.de ist eine Web-App zur Erstellung von Kommunikationsseiten mit
        Piktogrammen. Sie unterstützt Fachkräfte in der Unterstützten Kommunikation -
        Heilerziehungspfleger/innen, Logopäden, Sonderpädagogen, Therapeuten und Eltern -
        bei der Erstellung individueller Kommunikationshilfen.
      </p>
    </div>
  );
}
